import { useState } from 'react';
import { ScrollRevealWrapper } from '../../../components/feature/ScrollRevealWrapper';

const meditacoes = [
  {
    icon: 'ri-anchor-line',
    titulo: 'Ancoragem de Estado',
    duracao: '15 min',
    frequencia: '432 Hz',
    objetivo: 'Acesso imediato a estados de pico',
    descricao: 'Conecte-se com sua fonte de confiança e presença. Ative estados de alta performance sob demanda através de ancoragem neural.',
    beneficios: ['Confiança sob demanda', 'Clareza mental imediata', 'Presença executiva'],
  },
  {
    icon: 'ri-brain-line',
    titulo: 'Reprogramação Profunda',
    duracao: '20 min',
    frequencia: '528 Hz',
    objetivo: 'Substituição de crenças limitantes',
    descricao: 'Acesse estados theta para reprogramar padrões mentais limitantes e instalar novas crenças de alta performance.',
    beneficios: ['Crenças de performance', 'Mentalidade de crescimento', 'Autoconfiança consolidada'],
  },
  {
    icon: 'ri-focus-3-line',
    titulo: 'Clareza e Propósito',
    duracao: '12 min',
    frequencia: '963 Hz',
    objetivo: 'Alinhamento com objetivos estratégicos',
    descricao: 'Meditação de alta performance para clareza de visão, alinhamento com propósito e tomada de decisão estratégica.',
    beneficios: ['Clareza de visão', 'Decisões assertivas', 'Foco em prioridades'],
  },
  {
    icon: 'ri-sparkling-2-line',
    titulo: 'Estado de Pico',
    duracao: '10 min',
    frequencia: '741 Hz',
    objetivo: 'Ativação de alta performance',
    descricao: 'Protocolo rápido para entrar no estado de flow. Usado por líderes e atletas de elite para performance máxima consistente.',
    beneficios: ['Foco profundo', 'Energia sustentada', 'Performance de elite'],
  },
  {
    icon: 'ri-heart-pulse-line',
    titulo: 'Regulação Emocional',
    duracao: '18 min',
    frequencia: '639 Hz',
    objetivo: 'Equilíbrio e resiliência emocional',
    descricao: 'Protocolo de regulação do sistema nervoso autônomo. Reduz cortisol, restaura equilíbrio emocional e fortalece resiliência.',
    beneficios: ['Controle emocional', 'Redução de estresse', 'Resiliência aumentada'],
  },
  {
    icon: 'ri-compass-line',
    titulo: 'Visão do Futuro',
    duracao: '25 min',
    frequencia: '852 Hz',
    objetivo: 'Programação do eu ideal',
    descricao: 'Meditação de visualização avançada. Conecte-se com a versão mais poderosa de si mesma e programe seu sistema para alcançá-la.',
    beneficios: ['Visão clara do futuro', 'Intuição aguçada', 'Propósito consolidado'],
  },
];

export default function MeditacaoGuiada() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  return (
    <section className="py-28 bg-[#0a0a0a] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/25 to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_50%,rgba(201,164,64,0.05),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_50%,rgba(201,164,64,0.04),transparent_50%)]"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <ScrollRevealWrapper animation="fade-up">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-4 mb-6">
              <div className="h-px w-10 bg-gradient-to-r from-transparent to-gold-500/60"></div>
              <span className="text-gold-400 text-xs font-semibold tracking-[0.35em] uppercase font-['Montserrat']">
                Meditações Guiadas
              </span>
              <div className="h-px w-10 bg-gradient-to-l from-transparent to-gold-500/60"></div>
            </div>
            <h2 className="font-display text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
              Áudios de <span className="text-gold-400">Alta Performance</span>
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto leading-relaxed font-['Montserrat'] font-light">
              Protocolos de áudio guiado baseados em PNL clínica e neurofisiologia para reprogramação mental, regulação emocional e performance de elite
            </p>
          </div>
        </ScrollRevealWrapper>

        {/* Grid de meditações */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {meditacoes.map((m, i) => (
            <ScrollRevealWrapper key={i} animation="fade-up" delay={0.07 * i}>
              <div
                className="group bg-[#111111] border border-gold-500/10 rounded-2xl overflow-hidden hover:border-gold-500/35 transition-all duration-400 cursor-default"
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="p-7">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-5">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-300 ${hoveredCard === i ? 'bg-gold-500 border border-gold-400' : 'bg-gold-500/10 border border-gold-500/15'}`}>
                      <i className={`${m.icon} text-xl ${hoveredCard === i ? 'text-[#0a0a0a]' : 'text-gold-400'}`}></i>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-2.5 py-1 bg-gold-500/10 border border-gold-500/15 rounded-full text-gold-400 text-xs font-semibold font-['Montserrat']">
                        {m.frequencia}
                      </span>
                      <span className="flex items-center gap-1 text-white/30 text-xs font-['Montserrat']">
                        <i className="ri-time-line"></i>
                        {m.duracao}
                      </span>
                    </div>
                  </div>

                  <h3 className="font-display text-lg font-bold text-white mb-1">{m.titulo}</h3>
                  <p className="text-gold-400 text-xs font-['Montserrat'] mb-3">{m.objetivo}</p>
                  <p className="text-white/45 text-sm leading-relaxed font-['Montserrat'] mb-5">{m.descricao}</p>

                  {/* Benefícios */}
                  <div className="space-y-1.5">
                    {m.beneficios.map((b, bi) => (
                      <div key={bi} className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-fill text-gold-400 text-xs flex-shrink-0"></i>
                        <span className="text-white/50 text-xs font-['Montserrat']">{b}</span>
                      </div>
                    ))}
                  </div>

                  {/* Badge acesso */}
                  <div className="mt-5 pt-4 border-t border-gold-500/8 flex items-center gap-2">
                    <i className="ri-vip-crown-line text-gold-400 text-sm"></i>
                    <span className="text-gold-400/70 text-xs font-semibold font-['Montserrat'] tracking-wide uppercase">Acesso Premium</span>
                  </div>
                </div>
              </div>
            </ScrollRevealWrapper>
          ))}
        </div>

        {/* CTA */}
        <ScrollRevealWrapper animation="fade-up" delay={0.4}>
          <div className="text-center">
            <div className="inline-flex flex-col items-center gap-4">
              <a
                href="/planos"
                className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-[#0a0a0a] font-bold rounded-full hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap font-['Montserrat'] text-sm tracking-wide"
              >
                <i className="ri-headphone-line"></i>
                Acessar Todas as Meditações
                <i className="ri-arrow-right-line"></i>
              </a>
              <div className="flex items-center gap-5 text-white/30 text-xs font-['Montserrat']">
                <span className="flex items-center gap-1"><i className="ri-download-line text-gold-500/50"></i> Download disponível</span>
                <span className="flex items-center gap-1"><i className="ri-smartphone-line text-gold-500/50"></i> Acesso mobile</span>
                <span className="flex items-center gap-1"><i className="ri-infinity-line text-gold-500/50"></i> Acesso vitalício</span>
              </div>
            </div>
          </div>
        </ScrollRevealWrapper>
      </div>
    </section>
  );
}
